import CurrencyFormatter from "@/components/ui/CurrencyFormater";
import { colors } from "@/constant/data";
import { protectedApi } from "@/store/api/api";
import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";

// mini chart configs (unchanged visuals)
const columnCharthome2 = {
  series: [{ name: "Revenue", data: [40, 70, 45, 100, 75, 40, 80, 90] }],
  options: {
    chart: {
      toolbar: { show: false },
      offsetX: 0,
      offsetY: 0,
      zoom: { enabled: false },
      sparkline: { enabled: true },
    },
    plotOptions: { bar: { columnWidth: "60px", barHeight: "100%" } },
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    fill: { opacity: 1 },
    tooltip: { y: { formatter: (val) => "$ " + val + "k" } },
    yaxis: { show: false },
    xaxis: {
      show: false,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    colors: colors.warning,
    grid: { show: false },
  },
};
const columnCharthome3 = {
  series: [{ name: "Revenue", data: [40, 70, 45, 100, 75, 40, 80, 90] }],
  options: {
    chart: {
      toolbar: { show: false },
      offsetX: 0,
      offsetY: 0,
      zoom: { enabled: false },
      sparkline: { enabled: true },
    },
    plotOptions: { bar: { columnWidth: "60px", barHeight: "100%" } },
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    fill: { opacity: 1 },
    tooltip: { y: { formatter: (val) => "$ " + val + "k" } },
    yaxis: { show: false },
    xaxis: {
      show: false,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    colors: colors.info,
    grid: { show: false },
  },
};
const columnCharthome4 = {
  series: [{ name: "Revenue", data: [40, 70, 45, 100, 75, 40, 80, 90] }],
  options: {
    chart: {
      toolbar: { show: false },
      offsetX: 0,
      offsetY: 0,
      zoom: { enabled: false },
      sparkline: { enabled: true },
    },
    plotOptions: { bar: { columnWidth: "60px", barHeight: "100%" } },
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    fill: { opacity: 1 },
    tooltip: { y: { formatter: (val) => "$ " + val + "k" } },
    yaxis: { show: false },
    xaxis: {
      show: false,
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    colors: colors.success,
    grid: { show: false },
  },
};

const statistics = [
  {
    name: columnCharthome3,
    title: "Total Revenue",
    key: "totalRevenue",
    bg: "bg-[#E5F9FF] dark:bg-slate-900",
    text: "text-info-500",
    icon: "heroicons:shopping-cart",
  },
  {
    name: columnCharthome2,
    title: "Amount Paid",
    key: "amountPaid",
    bg: "bg-[#E5F9FF] dark:bg-slate-900",
    text: "text-[#5743BE]",
    icon: "heroicons:arrow-trending-up-solid",
  },
  {
    name: columnCharthome4,
    title: "your Percentage Split",
    key: "yourShare",
    bg: "bg-[#E5F9FF] dark:bg-slate-900",
    text: "text-warning-500",
    icon: "heroicons:cube",
  },
  {
    name: columnCharthome4,
    title: "Outstanding (Owed)",
    key: "amountOutstanding",
    bg: "bg-[#E5F9FF] dark:bg-slate-900",
    text: "text-warning-500",
    icon: "heroicons:cube",
  },
];

const GroupChart5 = ({
  totalRevenue: propTotalRevenue,
  amountPaid: propAmountPaid,
  amountOutstanding: propAmountOutstanding,
  percentageSplit: propPercentageSplit,
}) => {
  const [loading, setLoading] = useState(!propTotalRevenue);
  const [err, setErr] = useState(null);
  const [summary, setSummary] = useState({
    gross: 0,
    fee15: 0,
    share85: 0,
    paidToDate: 0,
    outstanding: 0,
  });

  // Use GHS as the currency
  const currency = "GHS";
  const displayCurrency = "GHS";

  // If props are provided, use them instead of fetching
  const hasProps = propTotalRevenue !== undefined;

  useEffect(() => {
    // If props are provided, skip API call
    if (hasProps) {
      setLoading(false);
      return;
    }

    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        setErr(null);
        // Add auth headers if needed
        const resp = await protectedApi.get("/api/organisers/summary");
        if (!mounted) return;
        const data = resp?.data || {};
        setSummary({
          gross: Number(data.gross || 0),
          fee15: Number(data.fee15 || 0),
          share85: Number(data.share85 || 0),
          paidToDate: Number(data.paidToDate || 0),
          outstanding: Number(data.outstanding || 0),
        });
      } catch (e) {
        if (!mounted) return;
        setErr(
          e?.response?.data?.error || e?.message || "Failed to load revenue"
        );
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [hasProps]);

  // Use props if provided, otherwise use fetched summary data
  const totalRevenue = propTotalRevenue ?? summary.gross;
  const amountPaid = propAmountPaid ?? summary.paidToDate;
  const amountOutstanding = propAmountOutstanding ?? summary.outstanding;

  // Calculate percentage split from props or from summary data
  const percentageSplit = propPercentageSplit
    ? propPercentageSplit
    : totalRevenue > 0
    ? {
        platform: Math.round((summary.fee15 / totalRevenue) * 100),
        user: Math.round((summary.share85 / totalRevenue) * 100),
      }
    : { platform: 15, user: 85 };

  const getUserSplitValue = () =>
    totalRevenue ? ((percentageSplit.user || 85) / 100) * totalRevenue : 0;

  if (loading) {
    return (
      <div className="bg-slate-50 dark:bg-slate-900 rounded p-4">
        <div className="animate-pulse h-5 w-40 bg-slate-200 dark:bg-slate-800 rounded mb-3" />
        <div className="animate-pulse h-6 w-28 bg-slate-200 dark:bg-slate-800 rounded mb-4" />
        <div className="animate-pulse h-12 w-[124px] bg-slate-200 dark:bg-slate-800 rounded" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300 rounded p-4">
        {err}
      </div>
    );
  }

  const values = {
    totalRevenue,
    amountPaid,
    yourShare: getUserSplitValue(),
    amountOutstanding,
  };

  return (
    <>
      {statistics.map((item, i) => (
        <div className="bg-slate-50 dark:bg-slate-900 rounded p-4" key={i}>
          <div className="text-slate-600 dark:text-slate-400 text-sm mb-1 font-medium">
            {item.title}
          </div>

          {item.key === "yourShare" ? (
            <div className="text-slate-900 dark:text-white text-lg font-medium">
              <CurrencyFormatter
                value={values[item.key] || 0}
                currency={currency}
              />
            </div>
          ) : (
            <div className="text-slate-900 dark:text-white text-lg font-medium">
              {displayCurrency} {Number(values[item.key] || 0).toLocaleString()}
            </div>
          )}

          <div className="ml-auto max-w-[124px]">
            <Chart
              options={item.name.options}
              series={item.name.series}
              type="bar"
              height="48"
              width="124"
            />
          </div>
        </div>
      ))}
    </>
  );
};

// ----- Admin metrics (unchanged visuals; plain JSX) -----
// ----- Admin metrics (unchanged visuals; plain JSX) -----
const adminStatistics = [
  {
    name: columnCharthome3,
    title: "Active Events Count",
    key: "activeEventsCount",
    bg: "bg-[#E5F9FF] dark:bg-slate-900",
    text: "text-info-500",
    icon: "heroicons:shopping-cart",
  },
  {
    name: columnCharthome3,
    title: "Pending Events Count",
    key: "pendingEventsCount",
    bg: "bg-[#E5F9FF] dark:bg-slate-900",
    text: "text-info-500",
    icon: "heroicons:shopping-cart",
  },
  {
    name: columnCharthome3,
    title: "Organisers Count",
    key: "organisersCount",
    bg: "bg-[#E5F9FF] dark:bg-slate-900",
    text: "text-info-500",
    icon: "heroicons:shopping-cart",
  },
  {
    name: columnCharthome3,
    title: "Payouts Count",
    key: "payoutsCount",
    bg: "bg-[#E5F9FF] dark:bg-slate-900",
    text: "text-info-500",
    icon: "heroicons:shopping-cart",
  },
  {
    name: columnCharthome4,
    title: "Total Revenue (All Active Events)",
    key: "totalRevenueAllEvents",
    bg: "bg-[#E5F9FF] dark:bg-slate-900",
    text: "text-success-500",
    icon: "heroicons:banknotes",
  },
  {
    name: columnCharthome2,
    title: "Total Payouts Made",
    key: "totalPayoutsMade",
    bg: "bg-[#E5F9FF] dark:bg-slate-900",
    text: "text-warning-500",
    icon: "heroicons:banknotes",
  },
  {
    name: columnCharthome2,
    title: "Total Due Organisers",
    key: "totalDueOrganisers",
    bg: "bg-[#E5F9FF] dark:bg-slate-900",
    text: "text-warning-500",
    icon: "heroicons:banknotes",
  },
  {
    name: columnCharthome3,
    title: "Last Updated",
    key: "updatedAt",
    bg: "bg-[#E5F9FF] dark:bg-slate-900",
    text: "text-info-500",
    icon: "heroicons:clock",
  },
];

export const AdminMetrics = ({ data }) => {
  console.log("data here", data);
  // helper formatters (local, no external deps)
  const isMoneyKey = (k) =>
    [
      "totalRevenueAllEvents",
      "totalPayoutsMade",
      "totalDueOrganisers",
    ].includes(k);

  const isDateKey = (k) => k === "updatedAt";

  const fmtMoney = (n) =>
    `CFA ${Number(n || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const fmtDate = (v) => {
    if (!v) return "—";
    // support Firestore Timestamp, millis, or ISO
    let d;
    if (typeof v?.toDate === "function") d = v.toDate();
    else if (typeof v === "number") d = new Date(v);
    else d = new Date(v);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
  };

  return (
    <>
      {adminStatistics.map((item, i) => {
        console.log("map");
        const raw = data[item.key];
        const display = isMoneyKey(item.key)
          ? fmtMoney(raw)
          : isDateKey(item.key)
          ? fmtDate(raw)
          : raw ?? 0;

        return (
          <div className="bg-slate-50 dark:bg-slate-900 rounded p-4" key={i}>
            <div className="text-slate-600 dark:text-slate-400 text-sm mb-1 font-medium">
              {item.title}
            </div>
            <div className="text-slate-900 dark:text-white text-lg font-medium">
              {display}
            </div>
            <div className="ml-auto max-w-[124px]">
              <Chart
                options={item.name.options}
                series={item.name.series}
                type="bar"
                height="48"
                width="124"
              />
            </div>
          </div>
        );
      })}
    </>
  );
};

export default GroupChart5;
